import { useCallback, useState } from 'react'
import type { ReactNode } from 'react'
import type { AttendanceRecord } from '@/api/learnerProfile'
import { GrowthAlertButton } from '@/components/admin/coaching/GrowthAlertButton'
import { GrowthAlertPreviewDialog } from '@/components/admin/coaching/GrowthAlertPreviewDialog'
import { useGrowthAlertPreview } from '@/hooks/coaching/useGrowthAlertPreview'

/**
 * admin 출결이력 → 성장 알림 진입점 공유 훅.
 * 선택된 행의 날짜로 미리보기 쿼리를 게이팅하고, 행별 액션 버튼과 다이얼로그를 제공한다.
 * 출결 이력은 레슨 진도와 분리되어 있으므로 하원 여부만 로컬에서 판단하고,
 * 완료 레슨 존재 여부는 날짜별 preview API가 completedAt 기준으로 판정한다.
 *
 * `dialog` 는 호출부가 직접 배치한다 — 모달에서는 onClose 오버레이의 "형제"로 렌더해야
 * 다이얼로그 백드롭 클릭이 부모 모달을 닫지 않는다(React 포털 이벤트 버블링 격리).
 */
export function useAttendanceGrowthAlert(userId: number): {
  renderActions: (rec: AttendanceRecord) => ReactNode
  dialog: ReactNode
} {
  const [growthAlertDate, setGrowthAlertDate] = useState<string | null>(null)
  const growthAlert = useGrowthAlertPreview(userId, growthAlertDate ?? undefined, growthAlertDate != null)

  const renderActions = useCallback(
    (rec: AttendanceRecord) => (
      <GrowthAlertButton
        compact
        checkOutAt={rec.checkOutAt}
        onClick={() => setGrowthAlertDate(rec.date)}
      />
    ),
    [],
  )

  const dialog =
    growthAlertDate != null ? (
      <GrowthAlertPreviewDialog
        preview={growthAlert.data}
        isLoading={growthAlert.isLoading}
        isError={growthAlert.isError}
        onClose={() => setGrowthAlertDate(null)}
      />
    ) : null

  return { renderActions, dialog }
}
